import React, { useState, useEffect, useRef, useCallback } from "react";
import classNames from "classnames";
import { Icon } from "@/components/ui/display/Icon";
import { IconButton } from "@/components/ui/buttons/IconButton";
import type { SearchInputProps } from "./types";
import styles from "./SearchInput.module.css";

/**
 * SearchInput component - A search input field with icon and clear button.
 * Supports debouncing and keyboard shortcuts.
 */
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value: controlledValue,
      placeholder = "Search...",
      onChange,
      onSearch,
      onClear,
      disabled = false,
      autoFocus = false,
      debounce = 0,
      appearance = "default",
      size = "md",
      className,
      "data-testid": testId,
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(controlledValue || "");
    const debounceRef = useRef<number | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync with controlled value
    useEffect(() => {
      if (controlledValue !== undefined) {
        setInternalValue(controlledValue);
      }
    }, [controlledValue]);

    // Handle ref forwarding
    useEffect(() => {
      if (ref) {
        if (typeof ref === "function") {
          ref(inputRef.current);
        } else {
          ref.current = inputRef.current;
        }
      }
    }, [ref]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInternalValue(newValue);

        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }

        if (debounce > 0) {
          debounceRef.current = window.setTimeout(() => {
            onChange?.(newValue);
          }, debounce);
        } else {
          onChange?.(newValue);
        }
      },
      [onChange, debounce],
    );

    const handleClear = useCallback(() => {
      setInternalValue("");
      onChange?.("");
      onClear?.();
      inputRef.current?.focus();
    }, [onChange, onClear]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onSearch?.(internalValue);
        } else if (e.key === "Escape") {
          handleClear();
        }
      },
      [internalValue, onSearch, handleClear],
    );

    return (
      <div
        className={classNames(styles.container, className, {
          [styles.clean]: appearance === "clean",
        })}
        data-size={size}
      >
        <Icon name="magnify" className={styles.searchIcon} aria-hidden />

        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          value={internalValue}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          data-testid={testId}
        />

        {internalValue && !disabled && (
          <IconButton
            icon={<Icon name="x" className={styles.clearIcon} aria-hidden />}
            variant="clear"
            size="sm"
            onClick={handleClear}
            aria-label="Clear search"
            className={styles.clearButton}
            data-testid={testId ? `${testId}-clear` : undefined}
          />
        )}
      </div>
    );
  },
);

SearchInput.displayName = "SearchInput";

export default SearchInput;
